import React, { useState, useRef, useCallback, useMemo } from 'react';
import { Entity } from '../types';
import EntityTypeSelector from './EntityTypeSelector';
import { getEntityColorClass } from '../utils/fileParser';

interface AnnotationCardProps {
  questionText: string;
  annotations: Entity[];
  availableEntityTypes: string[];
  onAnnotationsChange: (annotations: Entity[]) => void;
  customLabelColors?: Record<string, string>;
}

interface SelectionData {
  text: string;
  start: number;
  end: number;
  position: { top: number; left: number };
}

const AnnotationCard: React.FC<AnnotationCardProps> = ({
  questionText,
  annotations,
  availableEntityTypes,
  onAnnotationsChange,
  customLabelColors
}) => {
  const [selectionData, setSelectionData] = useState<SelectionData | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const textContainerRef = useRef<HTMLDivElement>(null);

  // Sort annotations by start position for proper rendering
  const sortedAnnotations = useMemo(() => {
    return [...annotations].sort((a, b) => (a.start || 0) - (b.start || 0));
  }, [annotations]);

  const handleMouseUp = useCallback(() => {
    const selection = window.getSelection();
    
    if (!selection || selection.isCollapsed || !textContainerRef.current) {
      return;
    }

    const rawSelected = selection.toString();
    if (!rawSelected) {
      return;
    }

    // Calculate character offsets by finding the position in the original question text
    const range = selection.getRangeAt(0);
    
    // Create a range for the entire container
    const preSelectionRange = range.cloneRange();
    preSelectionRange.selectNodeContents(textContainerRef.current);
    preSelectionRange.setEnd(range.startContainer, range.startOffset);
    
    // Get text before selection
    const textBeforeSelection = preSelectionRange.toString();
    
    const startRaw = textBeforeSelection.length;
    const endRaw = startRaw + rawSelected.length;
    const leadingWs = (rawSelected.match(/^\s*/) || [''])[0].length;
    const trailingWs = (rawSelected.match(/\s*$/) || [''])[0].length;
    const start = startRaw + leadingWs;
    const end = endRaw - trailingWs;

    // Get position for popup (fixed positioning, so use viewport coordinates)
    const rect = range.getBoundingClientRect();
    const position = {
      top: rect.bottom + 5,
      left: rect.left
    };

    if (process.env.NODE_ENV !== 'production') {
      const around = 20;
      const windowStart = Math.max(0, start - around);
      const windowEnd = Math.min(questionText.length, end + around);
      // eslint-disable-next-line no-console
      console.log('[AnnotateCard] mouseUp', {
        rawSelected,
        start,
        end,
        substring: questionText.substring(start, end),
        context: questionText.substring(windowStart, windowEnd)
      });
    }

    setSelectionData({
      text: questionText.substring(start, end),
      start,
      end,
      position
    });

    // Keep selection to avoid focus disruptions; popup will manage focus
  }, []);

  const handleEntityTypeSelect = useCallback((entityType: string) => {
    // Check editingIndex first - if we're editing, update the existing entity
    if (editingIndex !== null) {
      const updatedAnnotations = [...annotations];
      updatedAnnotations[editingIndex] = {
        ...updatedAnnotations[editingIndex],
        label: entityType
      };
      onAnnotationsChange(updatedAnnotations);
      setEditingIndex(null);
    } else if (selectionData) {
      // Otherwise, create a new entity with corrected offsets if needed
      let selStart = selectionData.start;
      let selEnd = selectionData.end;
      const selected = selectionData.text;
      if (questionText.substring(selStart, selEnd) !== selected && selected.length > 0) {
        const windowStart = Math.max(0, selStart - 100);
        const idx = questionText.indexOf(selected, windowStart);
        if (idx !== -1) {
          selStart = idx;
          selEnd = idx + selected.length;
        }
      }
      const newEntity: Entity = {
        text: selected,
        label: entityType,
        start: selStart,
        end: selEnd
      };
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log('[AnnotateCard] add entity', newEntity);
      }
      onAnnotationsChange([...annotations, newEntity]);
    }
    setSelectionData(null);
  }, [selectionData, editingIndex, annotations, onAnnotationsChange, questionText]);

  const handleCancel = useCallback(() => {
    setSelectionData(null);
    setEditingIndex(null);
  }, []);

  const handleDeleteAnnotation = useCallback((index: number) => {
    const updatedAnnotations = annotations.filter((_, i) => i !== index);
    onAnnotationsChange(updatedAnnotations);
  }, [annotations, onAnnotationsChange]);

  const handleEditAnnotation = useCallback((index: number, event?: React.MouseEvent) => {
    const annotation = annotations[index];
    
    if (!annotation) {
      return;
    }
    
    // Get position from the click event if available, otherwise use container
    let top = 200;
    let left = 200;
    
    if (event) {
      const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
      top = rect.bottom + 5;
      left = rect.left;
    } else if (textContainerRef.current) {
      const rect = textContainerRef.current.getBoundingClientRect();
      top = rect.bottom + 5;
      left = rect.left + 100;
    }
    
    setEditingIndex(index);
    setSelectionData({
      text: annotation.text,
      start: annotation.start || 0,
      end: annotation.end || 0,
      position: { top, left }
    });
  }, [annotations]);

  // Render text with highlighted annotations
  const renderAnnotatedText = () => {
    if (sortedAnnotations.length === 0) {
      return <span>{questionText}</span>;
    }

    const segments: JSX.Element[] = [];
    let lastEnd = 0;

    sortedAnnotations.forEach((annotation) => {
      const start = annotation.start || 0;
      const end = annotation.end || start + annotation.text.length;

      // Find the original index in the unsorted annotations array
      const originalIndex = annotations.findIndex(
        a => a.text === annotation.text && 
             a.label === annotation.label && 
             a.start === annotation.start && 
             a.end === annotation.end
      );

      // Add text before annotation
      if (start > lastEnd) {
        segments.push(
          <span key={`text-${lastEnd}`}>
            {questionText.substring(lastEnd, start)}
          </span>
        );
      }

      // Add annotated text (removed tooltip to avoid selection interference)
      const colorClass = getEntityColorClass(annotation.label, customLabelColors);
      const isCustomColor = colorClass.includes('|') || colorClass.startsWith('#') || colorClass.startsWith('rgb');
      
      // Parse custom color format: "bg:#hex|text:#hex|border:#hex"
      let bgColor, textColor, borderColor;
      if (colorClass.includes('|')) {
        const parts = colorClass.split('|');
        bgColor = parts[0].replace('bg:', '');
        textColor = parts[1].replace('text:', '');
        borderColor = parts[2] ? parts[2].replace('border:', '') : textColor; // Default to text color if border not specified
      } else if (isCustomColor) {
        // Legacy single color format
        bgColor = colorClass + '20';
        textColor = colorClass;
        borderColor = colorClass;
      }
      
      segments.push(
        <span
          key={`entity-${start}-${end}`}
          className={isCustomColor ? 'inline-block px-1 py-0.5 rounded cursor-pointer font-medium text-xs border-2' : `${colorClass} px-1 py-0.5 rounded cursor-pointer`}
          style={isCustomColor ? {
            backgroundColor: bgColor,
            color: textColor,
            borderColor: borderColor
          } : undefined}
          onClick={(e) => handleEditAnnotation(originalIndex, e)}
        >
          {questionText.substring(start, end)}
        </span>
      );

      lastEnd = end;
    });

    // Add remaining text
    if (lastEnd < questionText.length) {
      segments.push(
        <span key={`text-${lastEnd}`}>
          {questionText.substring(lastEnd)}
        </span>
      );
    }

    return <>{segments}</>;
  };

  return (
    <div className="p-3 rounded-lg border bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
      {/* Header */}
      <div className="mb-2">
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-gray-900 flex items-center">
            <svg className="w-4 h-4 mr-1 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Annotations
          </h4>
          <span className="text-xs text-gray-600">
            {annotations.length} {annotations.length === 1 ? 'entity' : 'entities'}
          </span>
        </div>
        <p className="text-xs text-gray-600 mt-1">
          Select text in the example above to annotate
        </p>
      </div>

      {/* Annotated Text removed: use main example text above */}

      {/* Entity List */}
      {annotations.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs font-medium text-gray-700">Annotations:</div>
          <div className="space-y-1">
            {sortedAnnotations.map((entity, idx) => {
              // Find the original index in the unsorted annotations array
              const originalIndex = annotations.findIndex(
                a => a.text === entity.text && 
                     a.label === entity.label && 
                     a.start === entity.start && 
                     a.end === entity.end
              );
              
              const colorClass = getEntityColorClass(entity.label, customLabelColors);
              const isCustomColor = colorClass.includes('|') || colorClass.startsWith('#') || colorClass.startsWith('rgb');
              
              // Parse custom color format: "bg:#hex|text:#hex|border:#hex"
              let bgColor, textColor, borderColor;
              if (colorClass.includes('|')) {
                const parts = colorClass.split('|');
                bgColor = parts[0].replace('bg:', '');
                textColor = parts[1].replace('text:', '');
                borderColor = parts[2] ? parts[2].replace('border:', '') : textColor; // Default to text color if border not specified
              } else if (isCustomColor) {
                // Legacy single color format
                bgColor = colorClass + '20';
                textColor = colorClass;
                borderColor = colorClass;
              }
              
              return (
                <div
                  key={`entity-list-${entity.start}-${entity.end}-${entity.text}-${entity.label}-${idx}`}
                  className="flex items-center justify-between text-xs bg-white rounded p-2 border border-purple-100"
                >
                  <div className="flex-1 flex items-center space-x-2">
                    <span 
                      className={isCustomColor ? 'inline-block px-2 py-1 rounded font-mono font-medium text-xs border-2' : `${colorClass} px-2 py-1 rounded font-mono`}
                      style={isCustomColor ? {
                        backgroundColor: bgColor,
                        color: textColor,
                        borderColor: borderColor
                      } : undefined}
                    >
                      {entity.text}
                    </span>
                    <span className="text-gray-400">→</span>
                    <span className="font-medium text-gray-700">{entity.label}</span>
                    {entity.start !== undefined && entity.end !== undefined && (
                      <span className="text-gray-400 text-xs">
                        ({entity.start}, {entity.end})
                      </span>
                    )}
                  </div>
                  <div className="flex items-center space-x-1 ml-2">
                    <button
                      onClick={(e) => handleEditAnnotation(originalIndex, e)}
                      className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                      title="Edit label"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDeleteAnnotation(originalIndex)}
                      className="p-1 text-red-600 hover:bg-red-50 rounded"
                      title="Delete"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Entity Type Selector Popup */}
      {(selectionData || editingIndex !== null) && (
        <EntityTypeSelector
          availableTypes={availableEntityTypes}
          onSelect={handleEntityTypeSelect}
          onCancel={handleCancel}
          position={selectionData?.position || { top: 0, left: 0 }}
          initialValue={editingIndex !== null ? annotations[editingIndex]?.label : undefined}
        />
      )}
    </div>
  );
};

export default AnnotationCard;

